import { memo } from 'react';
import { AdminFormRow, AdminFormSection } from '@ui';
import { CoverageSelect } from '../CoverageSelect/CoverageSelect';
import { ControlledCoverageSelect } from '../CoverageSelect/ControlledCoverageSelect';
import { useCoverageSearch } from '../CoverageSelect/useCoverageSearch';
import { useCoverageSection } from './useCoverageSection';

interface CoverageSectionProps {
  title: string;
  serviceRegionId: string;
  isReadOnly?: boolean;
}

export const CoverageSection = memo(({ title, serviceRegionId, isReadOnly = false }: CoverageSectionProps) => {
  const {
    countrySelectItems,
    countryNameByAlpha2,
    isAllCurrentlySelected,
    countrySelectedKeys,
    subdivisionItemsByAlpha2,
    handleCountrySelectionChange,
    handleCountryRemove,
    regionCountryCodes,
    selectedCountryCodes,
  } = useCoverageSection(serviceRegionId);

  const {
    searchValue: countrySearch,
    setSearchValue: setCountrySearch,
    filteredItems: filteredCountryItems,
  } = useCoverageSearch(countrySelectItems, countrySelectedKeys);

  return (
    <AdminFormSection title={title}>
      <AdminFormRow title="Countries">
        <CoverageSelect
          items={filteredCountryItems}
          aria-label="Countries"
          value={countrySelectedKeys}
          isDisabled={isReadOnly}
          disabledKeys={isAllCurrentlySelected ? regionCountryCodes : []}
          onSelectionChange={handleCountrySelectionChange}
          onRemove={handleCountryRemove}
          searchValue={countrySearch}
          onSearchValue={setCountrySearch}
        />
      </AdminFormRow>

      {selectedCountryCodes.map((alpha2) => (
        <AdminFormRow key={alpha2} title={countryNameByAlpha2[alpha2] ?? alpha2}>
          <ControlledCoverageSelect
            name={`coverage.${alpha2}`}
            items={subdivisionItemsByAlpha2[alpha2] ?? []}
            aria-label={`${countryNameByAlpha2[alpha2] ?? alpha2} Coverage`}
            isDisabled={isReadOnly}
          />
        </AdminFormRow>
      ))}
    </AdminFormSection>
  );
});

CoverageSection.displayName = 'CoverageSection';
